import React, { useState, useEffect } from 'react';
import { CheckCircle2, Circle, Trash2, Flame, Pencil, MessageSquare, Calendar } from 'lucide-react';
import * as Icons from 'lucide-react';
import { toggleHabitLog, getLogs, calculateStreak } from '../lib/storage';
import { triggerHabitComplete, triggerStreakMilestone } from '../lib/confetti';
import { toast } from 'sonner';
import { DAYS_OF_WEEK } from '../lib/avatars';

const HabitCard = ({ habit, onDelete, onToggle, onEdit, onAddNote }) => {
  const [isCompleted, setIsCompleted] = useState(false);
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [note, setNote] = useState('');

  // Helper function to format date in local timezone (YYYY-MM-DD)
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Check if habit is scheduled for today
  const isScheduledForToday = () => {
    if (!habit.scheduledDays || habit.scheduledDays.length === 0) return true; // Daily habit
    const today = new Date();
    const dayName = DAYS_OF_WEEK[today.getDay() === 0 ? 6 : today.getDay() - 1].short;
    return habit.scheduledDays.includes(dayName);
  };

  useEffect(() => {
    checkCompletion();
  }, [habit.id]);

  const checkCompletion = async () => {
    try {
      const logs = await getLogs();
      const today = formatDateLocal(new Date());
      const todayLog = logs.find(log => log.habitId === habit.id && log.date === today);
      setIsCompleted(!!todayLog);
      if (todayLog?.notes) {
        setNote(todayLog.notes);
      }
      
      const currentStreak = calculateStreak(habit.id, logs);
      setStreak(currentStreak);
    } catch (error) {
      console.error('Error checking completion:', error);
    }
  };

  const handleToggle = async (withNote = null) => {
    if (loading) return;
    
    try {
      setLoading(true);
      const today = formatDateLocal(new Date());
      const wasCompleted = isCompleted;
      await toggleHabitLog(habit.id, today, withNote);
      await checkCompletion();
      
      if (!wasCompleted) {
        // Trigger celebration
        triggerHabitComplete();
        
        // Check for streak milestones
        const logs = await getLogs();
        const newStreak = calculateStreak(habit.id, logs);
        if (newStreak > 0 && newStreak % 7 === 0) {
          // Milestone every 7 days
          setTimeout(() => triggerStreakMilestone(newStreak), 500);
          toast.success(`🔥 Amazing! ${newStreak} day streak!`);
        } else {
          toast.success('Great job! Keep it up!');
        }
      } else {
        toast.success('Habit unchecked');
      }
      
      if (onToggle) onToggle();
      setShowNoteInput(false);
    } catch (error) {
      console.error('Error toggling habit:', error);
      toast.error('Failed to update habit');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = () => {
    if (!isCompleted) {
      setShowNoteInput(true);
    } else if (onAddNote) {
      onAddNote(habit, note);
    }
  };

  const handleNoteSubmit = async () => {
    const noteToSave = note;
    await handleToggle(noteToSave);
    // Note: don't clear the note state here - checkCompletion will reload it from the server
  };

  const handleDelete = () => {
    onDelete(habit.id);
  };

  const handleEdit = () => {
    if (onEdit) onEdit(habit);
  };

  // Get the icon component
  const IconComponent = Icons[habit.icon] || Icons.Circle;
  const scheduledToday = isScheduledForToday();

  // If not scheduled for today, show dimmed card
  if (!scheduledToday) {
    return (
      <div
        data-testid={`habit-card-${habit.id}`}
        className="group flex items-center gap-4 p-4 gradient-card rounded-2xl border border-white/10 dark:border-white/5 opacity-50"
      >
        <div className="flex-shrink-0">
          <Circle className="w-7 h-7 text-slate-300 dark:text-slate-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div 
              className="w-8 h-8 rounded-xl flex items-center justify-center shadow-sm opacity-50"
              style={{ background: `linear-gradient(135deg, ${habit.color}20, ${habit.color}40)` }}
            >
              <IconComponent className="w-4 h-4" style={{ color: habit.color || '#3B82F6' }} />
            </div>
            <h3 className="font-medium text-slate-500 dark:text-slate-400">{habit.name}</h3>
          </div>
          <div className="flex items-center gap-1 ml-10 text-xs text-slate-400">
            <Calendar className="w-3 h-3" />
            <span>Scheduled: {habit.scheduledDays?.join(', ')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`habit-card-${habit.id}`}
      className="group flex flex-col gap-2 p-4 gradient-card rounded-2xl border border-white/20 dark:border-white/10 hover:shadow-xl transition-all duration-300 hover-glow"
    >
      <div className="flex items-center gap-4">
        {/* Checkbox */}
        <button
          onClick={() => handleToggle()}
          data-testid={`habit-checkbox-${habit.id}`}
          className="flex-shrink-0 transition-transform duration-200 hover:scale-110 active:scale-95"
          aria-label={`Toggle ${habit.name}`}
          disabled={loading}
        >
          {isCompleted ? (
            <CheckCircle2 
              className="w-7 h-7"
              style={{ color: habit.color || '#3B82F6' }}
            />
          ) : (
            <Circle className="w-7 h-7 text-slate-300 dark:text-slate-600" />
          )}
        </button>

        {/* Habit Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div 
              className="w-8 h-8 rounded-xl flex items-center justify-center shadow-sm"
              style={{ 
                background: `linear-gradient(135deg, ${habit.color}20, ${habit.color}40)` 
              }}
            >
              <IconComponent 
                className="w-4 h-4" 
                style={{ color: habit.color || '#3B82F6' }}
              />
            </div>
            <h3 
              data-testid={`habit-name-${habit.id}`}
              className={`font-medium text-slate-800 dark:text-slate-100 transition-all duration-200 ${
                isCompleted ? 'line-through opacity-60' : ''
              }`}
            >
              {habit.name}
            </h3>
          </div>
          {habit.category && (
            <span className="text-xs text-slate-500 dark:text-slate-400 ml-10">
              {habit.category}
            </span>
          )}
          {habit.scheduledDays && habit.scheduledDays.length > 0 && habit.scheduledDays.length < 7 && (
            <div className="flex items-center gap-1 ml-10 text-xs text-slate-400">
              <Calendar className="w-3 h-3" />
              <span>{habit.scheduledDays.join(', ')}</span>
            </div>
          )}
        </div>

        {/* Streak Counter */}
        {streak > 0 && (
          <div 
            data-testid={`habit-streak-${habit.id}`}
            className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-orange-400/20 to-red-400/20 rounded-full"
          >
            <Flame className="w-4 h-4 text-orange-500" />
            <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">{streak}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <button
            onClick={handleAddNote}
            data-testid={`habit-note-${habit.id}`}
            className="flex-shrink-0 p-2 text-slate-400 hover:text-purple-500 hover:bg-purple-500/10 rounded-xl transition-all duration-200"
            aria-label={`Add note to ${habit.name}`}
            title="Add note"
          >
            <MessageSquare className="w-4 h-4" />
          </button>
          <button
            onClick={handleEdit}
            data-testid={`habit-edit-${habit.id}`}
            className="flex-shrink-0 p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-500/10 rounded-xl transition-all duration-200"
            aria-label={`Edit ${habit.name}`}
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={handleDelete}
            data-testid={`habit-delete-${habit.id}`}
            className="flex-shrink-0 p-2 text-slate-400 hover:text-red-500 hover:bg-red-500/10 rounded-xl transition-all duration-200"
            aria-label={`Delete ${habit.name}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Note Input */}
      {showNoteInput && !isCompleted && (
        <div className="flex items-center gap-2 ml-11 mt-2">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note (optional)..."
            className="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            onKeyDown={(e) => e.key === 'Enter' && handleNoteSubmit()}
            autoFocus
          />
          <button
            onClick={handleNoteSubmit}
            className="px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg hover:from-blue-600 hover:to-purple-700"
          >
            Complete
          </button>
          <button
            onClick={() => setShowNoteInput(false)}
            className="px-3 py-2 text-sm text-slate-500 hover:text-slate-700"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Display existing note */}
      {isCompleted && note && (
        <div className="ml-11 mt-1 text-sm text-slate-500 dark:text-slate-400 italic">
          📝 {note}
        </div>
      )}
    </div>
  );
};

export default HabitCard;
